import React, { useState, useEffect } from "react";
import { database } from "../firebaseConfig";
import { ref, get } from "firebase/database";
import "./Footer.css";

const Footer = () => {
    const [footerConfig, setFooterConfig] = useState(null);
    const [aboutusConfig, setAboutusConfig] = useState(null);
    const [floatingIconsConfig, setFloatingIconsConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [inquireResult, setInquireResult] = useState("");

    useEffect(() => {
        const fetchFooterConfig = async () => {
            try {
                const footerSnap = await get(ref(database, "footerConfig"));
                if (footerSnap.exists()) {
                    const config = footerSnap.val();
                    setFooterConfig(config);

                    // Fetch related data only if they are enabled in footerConfig
                    const fetchData = [];
                    if (config.aboutUsEnabled) {
                        fetchData.push(get(ref(database, "aboutusconfig")));
                    }
                    if (config.contactDetailsEnabled || config.enquireFormEnabled) {
                        fetchData.push(get(ref(database, "floatingIconsConfig")));
                    }

                    const [aboutusSnap, floatingSnap] = await Promise.all(fetchData);

                    if (aboutusSnap && aboutusSnap.exists()) {
                        setAboutusConfig(aboutusSnap.val());
                    }
                    if (floatingSnap && floatingSnap.exists()) {
                        setFloatingIconsConfig(floatingSnap.val());
                    }
                }
            } catch (error) {
                console.error("Error fetching footer-related configs:", error);
            }
            setLoading(false);
        };

        fetchFooterConfig();
    }, []);

    if (loading) {
        return (
            <footer className="footer-section">
                <p></p>
            </footer>
        );
    }

    // Extract data based on enabled configurations
    const aboutUsText = aboutusConfig?.hero?.text || "About us content not available.";
    const locateUsText = footerConfig?.locateUs || "";
    const formToken = floatingIconsConfig?.inquire?.formToken || "";
    const showInquiryForm = floatingIconsConfig?.inquire?.showOnContactPage;

    // Collect visible contact items
    const contactItems = [];
    if (floatingIconsConfig && footerConfig?.contactDetailsEnabled) {
        Object.entries(floatingIconsConfig).forEach(([key, config]) => {
            if (config.showOnContactPage && key !== "inquire" && key !== "socialMedia") {
                contactItems.push({
                    label: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
                    value: config.number || config.address || "",
                });
            }
        });
    }

    // Handle Web3Forms Submission
    const onSubmitInquiry = async (event) => {
        event.preventDefault();
        setInquireResult("Sending...");

        const formData = new FormData(event.target);
        formData.append("access_key", formToken);

        try {
            const response = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                body: formData,
            });

            const data = await response.json();

            if (data.success) {
                setInquireResult("Form Submitted Successfully. Our team will reach out.");
                event.target.reset();
            } else {
                console.error("Error:", data);
                setInquireResult(data.message);
            }
        } catch (error) {
            console.error("Submission error:", error);
            setInquireResult("Form submission failed. Please contact us via phone or WhatsApp.");
        }
    };

    return (
        <footer
            className="footer-section"
            style={{ backgroundColor: footerConfig?.backgroundColor || "#ffffff" }} // Apply background color here
        >
            <div className="footer-container">
                {/* ABOUT US Column */}
                {footerConfig?.aboutUsEnabled && aboutusConfig && (
                    <div className="footer-column about-us">
                        <h3>ABOUT US</h3>
                        <p>
                            {aboutUsText.split(" ").slice(0, 10).join(" ") +
                                (aboutUsText.split(" ").length > 10 ? " ..." : "")}
                        </p>
                        <button className="read-more-btn" onClick={() => window.location.href = "/about"}>
                            Read More
                        </button>
                    </div>
                )}

                {/* LOCATE US Column */}
                {footerConfig?.showLocationEnabled && (
                    <div className="footer-column locate-us">
                        <h3>LOCATE US</h3>
                        <p>{locateUsText}</p>
                    </div>
                )}

                {/* LET'S TALK / CONTACT Column */}
                {footerConfig?.contactDetailsEnabled && (
                    <div className="footer-column lets-talk">
                        <h3>LET'S TALK</h3>
                        {contactItems.length > 0 && (
                            <div className="contact-info">
                                {contactItems.map((item, index) => (
                                    <p key={index}>
                                        <strong>{item.label}:</strong> {item.value}
                                    </p>
                                ))}
                            </div>
                        )}

                        {/* SOCIAL MEDIA Icons */}
                        {floatingIconsConfig?.socialMedia &&
                            floatingIconsConfig.socialMedia.some((social) => social.showOnContactPage) && (
                                <div style={{ marginTop: "15px" }}>
                                    <p style={{ marginBottom: "5px", fontWeight: "bold" }}>Follow Us:</p>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                        {floatingIconsConfig.socialMedia.map((social, idx) =>
                                            social.showOnContactPage ? (
                                                <a
                                                    key={idx}
                                                    href={social.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        display: "inline-block",
                                                        width: "24px",
                                                        height: "24px"
                                                    }}
                                                >
                                                    <img
                                                        src={social.iconUrl}
                                                        alt={social.platform || "social"}
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "contain",
                                                            borderRadius: "50%",
                                                        }}
                                                    />
                                                </a>
                                            ) : null
                                        )}
                                    </div>
                                </div>
                            )}
                    </div>
                )}

                {/* Inquiry Form Column */}
                {footerConfig?.enquireFormEnabled && showInquiryForm && (
                    <div className="footer-column lets-talk form">
                        <h3>ENQUIRE</h3>
                        <form onSubmit={onSubmitInquiry}>
                            <input type="hidden" name="access_key" value={formToken} />
                            <input type="text" name="name" placeholder="Your Name" required />
                            <input type="text" name="city" placeholder="Your City" required />
                            <input type="email" name="email" placeholder="Your Email" required />
                            <input type="text" name="contact" placeholder="Your Contact" required />
                            <textarea name="message" placeholder="Your Message" rows="3" required></textarea>
                            <button type="submit" className="submit-btn">Submit</button>
                            <span className="inquire-result">{inquireResult}</span>
                        </form>
                    </div>
                )}
            </div>

            {/* Footer Bottom */}
            <div className="footer-bottom">
                <p>© 2025 Marriott Pharmaceutica PVT. LTD. All rights reserved.</p>
                <p style={{ fontSize: "0.7rem", marginTop: "5px", opacity: "0.8" }}>
                    Creator:{" "}
                    <a
                        href="https://www.adityasureshpatil.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#e30045", textDecoration: "none", fontWeight: "bold" }}
                    >
                        Aditya S Patil
                    </a>
                </p>
            </div>
        </footer>
    );
};

export default Footer;
